import * as ACData from 'adaptivecards-templating';
import {
  ProcessAutomationRun,
  ProcessAutomationStatus,
  ProcessAutomationWorkflow,
} from './model/process-automation';
import {
  CCF_PROCESS_AUTOMATION_KEY,
  finishWorkflowRun,
  isRunActive,
  patchWorkflowRun,
  pauseWorkflowRun,
  resumeWorkflowRun,
  startWorkflowRun,
  submitWorkflowCard,
} from './+state/ccf-process-automation.slice';
import {
  getActiveContactID,
  getActiveInteractionID,
  updateTwitterAgentReply,
} from '../../ccf-inbox/ccf-inbox.slice';

/** Simulated Cognigy processing time after the agent confirms the form */
const SUBMIT_PROCESSING_MS = 2400;
/** Delay before Copilot asks the next question */
const COPILOT_MESSAGE_DELAY_MS = 1500;
/** Delay before the scripted customer answer appears */
const CUSTOMER_REPLY_DELAY_MS = 2500;

/** Chat sender used for Copilot messages - rendered by the chat as Copilot */
const COPILOT_CHAT_SENDER = 'bot@nice.com';
/** Chat sender used for agent messages */
const AGENT_CHAT_SENDER = 'customer.service@delta.com';

type Dispatch = (action: any) => any;
type GetState = () => any;
type ChatSender = 'copilot' | 'customer' | 'agent';

/**
 * Builds the templating context available to workflow cards and output.
 * Customer fields act as the pre-fill source, later steps can also use any
 * value collected earlier (agent input or customer answers).
 * @param contact - active contact
 * @param collectedValues - values collected so far
 * @example buildTemplateContext(contact, values)
 * @returns templating context
 */
export const buildTemplateContext = (
  contact: any,
  collectedValues: Record<string, any> = {}
): Record<string, any> => {
  const customer = contact?.customer ?? {};
  return {
    customerFirstName: customer.firstName ?? '',
    customerLastName: customer.lastName ?? '',
    customerName: [customer.firstName, customer.lastName]
      .filter(Boolean)
      .join(' '),
    accountNumber: customer.accountNumber ?? '',
    phoneNumber: customer.phoneNo ?? '',
    email: customer.emailId ?? '',
    balance: customer.balance ?? '',
    contactId: contact?.id ?? '',
    ...collectedValues,
  };
};

/**
 * Expands dollar-brace token placeholders of an Adaptive Card / output object
 * @param payload - card or output JSON
 * @param context - templating context
 * @example expandTemplate(card, context)
 * @returns expanded JSON (original payload when expansion fails)
 */
export const expandTemplate = <T>(payload: T, context: Record<string, any>) => {
  try {
    const template = new ACData.Template(payload);
    return template.expand({ $root: context }) as T;
  } catch (e) {
    console.log('[process-automation] template expansion failed', e);
    return payload;
  }
};

/**
 * Returns a copy of the card with input values set from `values`, matched by
 * input id. Used for the pre-filled / Copilot-filled form.
 * @param card - Adaptive Card JSON
 * @param values - field id to value
 * @example prefillCard(card, payload)
 * @returns Adaptive Card JSON
 */
export const prefillCard = (
  card: Record<string, any>,
  values: Record<string, any>
): Record<string, any> => {
  /**
   * Walks the card tree and sets matching input values
   * @param node - card element
   * @example fill(node)
   * @returns element copy
   */
  const fill = (node: any): any => {
    if (Array.isArray(node)) return node.map(fill);
    if (!node || typeof node !== 'object') return node;
    const copy: Record<string, any> = {};
    Object.keys(node).forEach((key) => {
      copy[key] = fill(node[key]);
    });
    if (
      typeof copy.type === 'string' &&
      copy.type.startsWith('Input.') &&
      copy.id &&
      values[copy.id] !== undefined &&
      values[copy.id] !== ''
    ) {
      const value = values[copy.id];
      // Input.Number only takes a JSON number as its default value
      copy.value =
        copy.type === 'Input.Number' && Number.isFinite(Number(value))
          ? Number(value)
          : String(value);
    }
    return copy;
  };
  return fill(card);
};

/**
 * Replaces the card actions with the task actions of the current screen.
 * @param card - Adaptive Card JSON
 * @param actions - list of title / command pairs
 * @example withTaskActions(card, [payload])
 * @returns Adaptive Card JSON
 */
export const withTaskActions = (
  card: Record<string, any>,
  actions: { title: string; command: string }[]
) => ({
  ...card,
  actions: actions.map((action, index) => ({
    type: 'Action.Submit',
    title: action.title,
    style: index === actions.length - 1 ? 'positive' : 'default',
    data: { paCommand: action.command },
  })),
});

/**
 * Reads the active task of a contact from the store
 * @param getState - GetState
 * @param contactId - string
 * @example getActiveRun(getState, contactId)
 * @returns ProcessAutomationRun | undefined
 */
const getActiveRun = (getState: GetState, contactId: string) =>
  getState()?.[CCF_PROCESS_AUTOMATION_KEY]?.sessions?.[contactId]?.activeRun as
    | ProcessAutomationRun
    | undefined;

/**
 * Returns the run while it is still the active instance
 * @param getState - GetState
 * @param contactId - string
 * @param runId - string
 * @example isSameActiveRun(getState, contactId, runId)
 * @returns the run when still active
 */
const isSameActiveRun = (
  getState: GetState,
  contactId: string,
  runId: string
) => {
  const run = getActiveRun(getState, contactId);
  return run && run.runId === runId && isRunActive(run) ? run : undefined;
};

/**
 * Posts a message in the chat of the contact. Only the contact on screen can
 * receive messages, so nothing is posted after the agent switched contacts.
 * @param getState - GetState
 * @param dispatch - Dispatch
 * @param contact - contact of the task
 * @param sender - copilot | customer | agent
 * @param text - message text
 * @example postChatMessage(getState, dispatch, contact, 'copilot', 'Hi')
 * @returns true when the message was posted
 */
const postChatMessage = (
  getState: GetState,
  dispatch: Dispatch,
  contact: any,
  sender: ChatSender,
  text: string
) => {
  const state = getState();
  if (!text || String(getActiveContactID(state)) !== String(contact?.id)) {
    return false;
  }
  const isCustomer = sender === 'customer';
  let emailAddress = AGENT_CHAT_SENDER;
  if (sender === 'copilot') emailAddress = COPILOT_CHAT_SENDER;
  if (isCustomer) emailAddress = contact?.customer?.emailId;
  dispatch(
    updateTwitterAgentReply({
      interactionId: getActiveInteractionID(state),
      replyMessage: {
        messageId: `${new Date().toString()}-${Math.random()}`,
        postId: `chat_process_automation_${contact?.id}`,
        isOwn: true,
        createdAt: new Date().toUTCString(),
        messageContent: { payload: { text } },
        reactionStatistics: { likes: 0 },
        authorUser: {
          firstName: isCustomer ? contact?.customer?.firstName : '',
          surname: isCustomer ? contact?.customer?.lastName : '',
          emailAddress,
          loginUsername: isCustomer ? contact?.customer?.twitterId : '',
        },
        attachments: [],
        conferenceMsg: false,
      },
    })
  );
  scrollChatToLatestMessage();
  return true;
};

/**
 * Keeps the newest task message in view in the chat transcript. Runs after
 * the message has rendered so the scroll height includes it.
 * @example scrollChatToLatestMessage()
 */
const scrollChatToLatestMessage = () => {
  setTimeout(() => {
    const bodyContainer = document.getElementById('bodyContainer');
    bodyContainer?.scroll({
      top: bodyContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, 100);
};

/**
 * Finishes the task with the success output
 * @param workflow - ProcessAutomationWorkflow
 * @param contact - active contact
 * @param runId - string
 * @example dispatch(completeWorkflow(workflow, contact, runId))
 */
const completeWorkflow =
  (workflow: ProcessAutomationWorkflow, contact: any, runId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const contactId = String(contact?.id ?? '');
    const run = isSameActiveRun(getState, contactId, runId);
    if (!run) return;
    dispatch(
      finishWorkflowRun({
        contactId,
        runId,
        status: ProcessAutomationStatus.SUCCESS,
        output: expandTemplate(
          workflow.successOutput,
          buildTemplateContext(contact, run.collectedValues)
        ),
      })
    );
  };

/**
 * Plays the Copilot-led conversation one half-turn at a time. Copilot
 * asks in the chat, the scripted customer answers, the answer fills the form.
 * Stops while paused, when an answer needs the agent, or when the agent is on
 * another contact (the task is paused then).
 * @param workflow - ProcessAutomationWorkflow
 * @param contact - active contact
 * @param runId - string
 * @example dispatch(runConversation(workflow, contact, runId))
 */
const runConversation =
  (workflow: ProcessAutomationWorkflow, contact: any, runId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const contactId = String(contact?.id ?? '');
    const run = isSameActiveRun(getState, contactId, runId);
    if (
      !run ||
      run.status !== ProcessAutomationStatus.IN_PROGRESS ||
      run.requiresAttention ||
      run.isReviewing
    ) {
      return;
    }
    const turn = (workflow.conversation ?? [])[run.conversationIndex];
    if (!turn) {
      // Information is complete - the agent reviews the filled form
      dispatch(
        patchWorkflowRun({
          contactId,
          runId,
          changes: { isReviewing: true },
          status: ProcessAutomationStatus.BLOCKED,
        })
      );
      return;
    }
    const { conversationIndex, turnStage } = run;
    setTimeout(
      () => {
        const latest = isSameActiveRun(getState, contactId, runId);
        if (
          !latest ||
          latest.status !== ProcessAutomationStatus.IN_PROGRESS ||
          latest.conversationIndex !== conversationIndex ||
          latest.turnStage !== turnStage
        ) {
          return;
        }
        const context = buildTemplateContext(contact, latest.collectedValues);
        const text = expandTemplate(
          {
            text:
              turnStage === 'copilot'
                ? turn.copilotMessage
                : turn.customerReply,
          },
          context
        ).text;
        // An empty half-turn is skipped; a message that could not be posted
        // means the agent is on another contact, so the task pauses
        const isPosted =
          !text?.trim() ||
          postChatMessage(getState, dispatch, contact, turnStage, text);
        if (!isPosted) {
          dispatch(pauseWorkflowRun({ contactId, runId }));
          return;
        }
        if (turnStage === 'copilot') {
          dispatch(
            patchWorkflowRun({
              contactId,
              runId,
              changes: { turnStage: 'customer' },
            })
          );
        } else {
          dispatch(
            patchWorkflowRun({
              contactId,
              runId,
              values: turn.fills,
              changes: {
                conversationIndex: conversationIndex + 1,
                turnStage: 'copilot',
                requiresAttention: !!turn.needsAttention,
              },
              status: turn.needsAttention
                ? ProcessAutomationStatus.BLOCKED
                : undefined,
            })
          );
        }
        dispatch(runConversation(workflow, contact, runId));
      },
      turnStage === 'copilot'
        ? COPILOT_MESSAGE_DELAY_MS
        : CUSTOMER_REPLY_DELAY_MS
    );
  };

/**
 * Starts a task for the contact (manual or auto run)
 * @param workflow - ProcessAutomationWorkflow
 * @param contact - active contact
 * @param initialValues - values entered in "View Form" before Run
 * @example dispatch(startProcessAutomationWorkflow(workflow, contact))
 */
export const startProcessAutomationWorkflow =
  (
    workflow: ProcessAutomationWorkflow,
    contact: any,
    initialValues: Record<string, any> = {}
  ) =>
  (dispatch: Dispatch, getState: GetState) => {
    const contactId = String(contact?.id ?? '');
    if (!contactId || isRunActive(getActiveRun(getState, contactId))) return;

    const runId = `pa-run-${Date.now()}`;
    dispatch(
      startWorkflowRun({
        contactId,
        run: {
          runId,
          workflowId: workflow.id,
          workflowName: workflow.name,
          status: ProcessAutomationStatus.IN_PROGRESS,
          conversationIndex: 0,
          turnStage: 'copilot',
          collectedValues: {
            referenceNumber: `${(workflow.intentName || 'TSK')
              .slice(0, 3)
              .toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`,
            ...initialValues,
          },
          startedAt: Date.now(),
        },
      })
    );
    dispatch(runConversation(workflow, contact, runId));
  };

/**
 * Sends the reviewed form to "Cognigy" and completes the task after the
 * processing time
 * @param workflow - ProcessAutomationWorkflow
 * @param contact - active contact
 * @param runId - string
 * @param values - confirmed form values
 * @example dispatch(submitProcessAutomationCard(workflow, contact, runId, values))
 */
export const submitProcessAutomationCard =
  (
    workflow: ProcessAutomationWorkflow,
    contact: any,
    runId: string,
    values: Record<string, any>
  ) =>
  (dispatch: Dispatch, getState: GetState) => {
    const contactId = String(contact?.id ?? '');
    const run = isSameActiveRun(getState, contactId, runId);
    if (!run || run.status !== ProcessAutomationStatus.BLOCKED) return;
    dispatch(submitWorkflowCard({ contactId, runId, values }));
    setTimeout(
      () => dispatch(completeWorkflow(workflow, contact, runId)),
      SUBMIT_PROCESSING_MS
    );
  };

/**
 * Resumes a paused task, or continues a task that needed the agent's
 * attention
 * @param workflow - ProcessAutomationWorkflow
 * @param contact - active contact
 * @param runId - string
 * @example dispatch(resumeProcessAutomationWorkflow(workflow, contact, runId))
 */
export const resumeProcessAutomationWorkflow =
  (workflow: ProcessAutomationWorkflow, contact: any, runId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const contactId = String(contact?.id ?? '');
    let run = isSameActiveRun(getState, contactId, runId);
    if (!run) return;
    if (run.status === ProcessAutomationStatus.PAUSED) {
      dispatch(resumeWorkflowRun({ contactId, runId }));
    }
    run = isSameActiveRun(getState, contactId, runId);
    if (run?.requiresAttention) {
      dispatch(
        patchWorkflowRun({
          contactId,
          runId,
          changes: { requiresAttention: false },
          status: ProcessAutomationStatus.IN_PROGRESS,
        })
      );
    }
    run = isSameActiveRun(getState, contactId, runId);
    if (run?.status === ProcessAutomationStatus.IN_PROGRESS) {
      dispatch(runConversation(workflow, contact, runId));
    }
  };

/**
 * Cancels (ends) the active task of the contact
 * @param contactId - string
 * @param runId - string
 * @example dispatch(cancelProcessAutomationWorkflow(contactId, runId))
 */
export const cancelProcessAutomationWorkflow =
  (contactId: string, runId: string) => (dispatch: Dispatch) => {
    dispatch(
      finishWorkflowRun({
        contactId,
        runId,
        status: ProcessAutomationStatus.CANCELLED,
      })
    );
  };
